import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { EshopPricesFields } from '@/forms/components/EshopPricesFields'
import { EshopPaymentMethod } from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { buildPriceRows, buildPricesPayload } from '@/lib/eshop/prices'
import { components } from '@invo.space/shared'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

const schema = z
  .object({
    type: z.string(),
    isEnabled: z.boolean(),
    invoicingBankAccountId: z.string(),
    cardProviderKey: z.string(),
    prices: z.array(
      z.object({
        currency: z.string(),
        amount: z.number().min(0),
        freeFrom: z.number().min(0).nullable(),
      }),
    ),
  })
  .refine(
    (values) =>
      !(
        values.type === 'bankTransfer' &&
        values.isEnabled &&
        values.invoicingBankAccountId === ''
      ),
    {
      path: ['invoicingBankAccountId'],
      message: 'bankAccountRequired',
    },
  )

type PaymentMethodFormValues = z.infer<typeof schema>

interface EshopPaymentMethodFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  method: EshopPaymentMethod | null
  currencies: string[]
  bankAccounts: InvoicingBankAccount[]
  personalPickupEnabled: boolean
  onSuccess: () => void
}

export function EshopPaymentMethodForm({
  open,
  onOpenChange,
  method,
  currencies,
  bankAccounts,
  personalPickupEnabled,
  onSuccess,
}: EshopPaymentMethodFormProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.paymentMethods',
  })
  const { t: te } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })

  const form = useForm<PaymentMethodFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      type: '',
      isEnabled: false,
      invoicingBankAccountId: '',
      cardProviderKey: '',
      prices: [],
    },
  })

  useEffect(() => {
    if (!open || !method) return
    form.reset({
      type: method.type,
      isEnabled: method.isEnabled,
      invoicingBankAccountId: method.invoicingBankAccountId ?? '',
      cardProviderKey: method.cardProviderKey ?? '',
      prices: buildPriceRows(currencies, method.prices),
    })
  }, [open, method, currencies, form])

  if (!method) return null

  // Backend rejects enabling pay-on-pickup while personal pickup delivery
  // is disabled, so prevent it upfront with an explanation.
  const payOnPickupBlocked =
    method.type === 'payOnPickup' && !personalPickupEnabled && !method.isEnabled

  const onSubmit = async (values: PaymentMethodFormValues) => {
    try {
      await API.patch(`/api/v2/payment-methods/${method.type}`, {
        isEnabled: values.isEnabled,
        prices: buildPricesPayload(values.prices),
        ...(method.type === 'bankTransfer' && values.invoicingBankAccountId
          ? { invoicingBankAccountId: values.invoicingBankAccountId }
          : {}),
        ...(method.type === 'cardPayment'
          ? { cardProviderKey: values.cardProviderKey }
          : {}),
      })
      toast.success(t('edit.success'))
      onOpenChange(false)
      onSuccess()
    } catch (error: unknown) {
      toast.error(t('edit.error'), {
        description: extractErrorMessage(error),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(`types.${method.type}`)}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            <FormField
              control={form.control}
              name="isEnabled"
              render={({ field }) => (
                <FormItem className="rounded-lg border p-3">
                  <div className="flex flex-row items-center justify-between">
                    <FormLabel>{t('fields.isEnabled')}</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={payOnPickupBlocked}
                      />
                    </FormControl>
                  </div>
                  {payOnPickupBlocked && (
                    <FormDescription>
                      {t('fields.payOnPickupBlocked')}
                    </FormDescription>
                  )}
                </FormItem>
              )}
            />

            {method.type === 'bankTransfer' && (
              <FormField
                control={form.control}
                name="invoicingBankAccountId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.bankAccount')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={t('fields.bankAccountPlaceholder')}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {bankAccounts.map((account) => (
                          <SelectItem key={account.id} value={account.id!}>
                            {account.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.invoicingBankAccountId && (
                      <FormDescription className="text-destructive">
                        {t('fields.bankAccountRequired')}
                      </FormDescription>
                    )}
                    {bankAccounts.length === 0 && (
                      <FormDescription>
                        {t('fields.noBankAccounts')}
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            )}

            {method.type === 'cardPayment' && (
              <FormField
                control={form.control}
                name="cardProviderKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.cardProviderKey')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('fields.cardProviderKeyPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('fields.cardProviderKeyDescription')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div>
              <Label className="mb-2 block">{t('fields.prices')}</Label>
              <p className="text-muted-foreground mb-2 text-sm">
                {t('fields.pricesDescription')}
              </p>
              <EshopPricesFields control={form.control} />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {te('actions.cancel')}
              </Button>
              <Button type="submit">{te('actions.save')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
